import * as React from "react";
import { Text, Stack, Heading, Box } from "@chakra-ui/react";

import CONDITIONS from "scenes/TermsAndConditions/conditions";
import MetaHead from "components/MetaHead";
import Container from "components/Chakra/Container";
import CustomLink from "components/Chakra/CustomLink";
import Checkbox from "components/Chakra/Checkbox";
import { IconDownloadFile } from "@ud/icons";

interface IProps {
  activeTab: keyof typeof CONDITIONS;
}

const TermsAndConditions: React.FunctionComponent<IProps> = ({ activeTab }) => (
  <>
    <MetaHead
      title="Obchodní podmínky"
      description="Společnost UlovDomov.cz s.r.o., se sídlem: Lazaretní 925/9, 615 00 Brno, IČ: 29300631, zapsaná v obchodním rejstříku vedeném Krajským soudem v Brně, sp. zn. C72444"
      canonicalUrl="https://www.ulovdomov.cz/obchodni-podminky"
    />
    <Container>
      <Heading as="h1" mt="90px">
        Obchodní podmínky
      </Heading>
      <Stack
        mt="8"
        mb={{ base: "12", md: "16" }}
        spacing="20px"
        direction={{ base: "column", md: "row" }}
        alignItems="center"
        justify="center"
      >
        <CustomLink to="/obchodni-podminky">
          <Checkbox
            isChecked={activeTab === "personal"}
            label={<Text>Pro fyzické osoby</Text>}
          />
        </CustomLink>
        <CustomLink to="/obchodni-podminky-pro-rk">
          <Checkbox
            isChecked={activeTab === "bussines"}
            label={<Text>Pro realitní kanceláře</Text>}
          />
        </CustomLink>
        <CustomLink to="/obchodni-podminky-pro-cc">
          <Checkbox
            isChecked={activeTab === "credit"}
            label={<Text>Pro službu Prověření rizikovosti osob</Text>}
          />
        </CustomLink>
      </Stack>
      {activeTab === "bussines" && (
        <Stack
          justify="center"
          direction={{ base: "column", md: "row" }}
          alignItems="center"
          mt="-6"
          mb="6"
          spacing="4"
        >
          <CustomLink
            href="/vop/vop-pro-rk-2017.pdf"
            variant="button_outline"
            fontSize="16px"
            alignContent="center"
            borderRadius="full"
            px={{ base: "16px", md: "24px" }}
            isExternal
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            leftIcon={<IconDownloadFile fillWith="ud-white" mr="2" />}
            download
          >
            Stáhnout VOP 2017
          </CustomLink>
          <CustomLink
            href="/vop/vop-pro-rk-2024.pdf"
            variant="button_outline"
            fontSize="16px"
            alignContent="center"
            borderRadius="full"
            px={{ base: "16px", md: "24px" }}
            isExternal
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            leftIcon={<IconDownloadFile fillWith="ud-white" mr="2" />}
            download
          >
            Stáhnout VOP 2024
          </CustomLink>
          <CustomLink
            href="/vop/vop-pro-rk-2025.pdf"
            variant="button_outline"
            fontSize="16px"
            alignContent="center"
            borderRadius="full"
            px={{ base: "16px", md: "24px" }}
            isExternal
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            leftIcon={<IconDownloadFile fillWith="ud-white" mr="2" />}
            download
          >
            Stáhnout VOP 2025
          </CustomLink>
        </Stack>
      )}
      <Box
        px="5%"
        mb="100px"
        borderColor="ud-grey-200"
        borderWidth="1px"
        borderRadius="8px"
        py="10"
      >
        {activeTab !== "credit"
          ? CONDITIONS[activeTab].map((law, lawIndex) => (
              <Stack key={lawIndex} spacing="0" align="flex-start">
                <Heading variant="large_2" as="h2">
                  {lawIndex + 1}. {law.title}
                </Heading>
                <Stack
                  key={lawIndex}
                  spacing="0"
                  align="flex-start"
                  pt="10px"
                  pb="30px"
                >
                  {law.paragraphs.map((paragraph, paragraphIndex) => (
                    <Stack
                      key={paragraphIndex}
                      direction="row"
                      flexWrap="nowrap"
                      py="8px"
                      pl="10px"
                    >
                      <Text fontWeight="bold">{paragraphIndex + 1}.</Text>
                      <Text
                        sx={{ a: { color: "ud-primary" } }}
                        dangerouslySetInnerHTML={{
                          __html: paragraph,
                        }}
                      />
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            ))
          : CONDITIONS[activeTab].map((section) => (
              <Stack key={section.letter} direction="column" mt="50px">
                <Box>
                  <Heading as="h2" variant="h3" mb="20px">
                    ČÁST {section.letter}.
                  </Heading>
                  {section.text && <Text pb="30px">{section.text}</Text>}
                </Box>
                {section.laws &&
                  section.laws.map((law, lawIndex) => (
                    <Stack key={lawIndex} pb="20px">
                      <Stack>
                        <Heading mt="10px" mb="20px" variant="large_2" as="h3">
                          {lawIndex + 1}. {law.title}
                        </Heading>
                        <Stack
                          spacing="20px"
                          pl="15px"
                          direction="column"
                          align="flex-start"
                        >
                          {law.paragraphs.map((paragraph, paragraphIndex) => (
                            <Stack
                              direction="column"
                              align="flex-start"
                              key={paragraph.text}
                            >
                              <Stack
                                direction="row"
                                mb="10px"
                                justify="space-between"
                              >
                                <Text fontWeight="bold">
                                  {paragraphIndex + 1}.
                                </Text>
                                <Text
                                  sx={{ a: { color: "ud-primary" } }}
                                  dangerouslySetInnerHTML={{
                                    __html: paragraph.text,
                                  }}
                                />
                              </Stack>
                              <Stack
                                direction="column"
                                pl="30px"
                                spacing="10px"
                              >
                                <>
                                  {paragraph.lines &&
                                    paragraph.lines.map((l) => (
                                      <Text
                                        key={l}
                                        variant="small_2"
                                        sx={{ a: { color: "ud-primary" } }}
                                        dangerouslySetInnerHTML={{ __html: l }}
                                      />
                                    ))}
                                </>
                              </Stack>
                            </Stack>
                          ))}
                        </Stack>
                      </Stack>
                    </Stack>
                  ))}
              </Stack>
            ))}
      </Box>
    </Container>
  </>
);

TermsAndConditions.defaultProps = {
  activeTab: "personal",
};

export default TermsAndConditions;
